import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../../../components/ui/Button";
import { useI18n } from "../../../i18n";
import { createClassifier } from "../lib/ai/create-classifier";
import type { Classifier } from "../lib/ai/classifier";

/** 이 아래는 '모르겠음' — 엉뚱한 클래스로 보드를 움직이지 않게(Campus 패널과 같다) */
export const CONF_MIN = 0.55;
const INPUT = 224;
let seq = 0;
const newClass = (name: string) => ({ id: `c${++seq}`, name });

/**
 * 놀이터 진짜 카메라(PICNIC-MB-FIELD-001 §6 3단계 · §7 결정 4) — Campus 의 AI 모델 패널을 놀이터로 옮긴 것. <b>온디바이스 · 무전송(P-PA2)</b> —
 * 카메라 프레임은 브라우저 안에서만 학습 · 추론한다. 모델도 이 기기에만 있다(Campus 처럼 저장하지 않는다 — 공유 태블릿에서 섞이지 않게).
 * 클래스 이름은 <b>아이 프로그램이 기다리는 이름</b>으로 미리 채운다(이름이 맞아야 '내 모델이 ⟨사과⟩로 보이면' 이 깨어난다).
 * 동의 관문(AiCardsPanel) 안에서만 그려진다 — ok 가 아니면 이 부품이 없으므로 카메라에 닿는 코드가 돌지 않는다.
 * 지금 보이는 클래스는 `onClass` 로 나간다(약 8번/초 · 신뢰도 문턱 아래면 빈 이름).
 */
export function AiCameraPanel({ programClasses, onClass, su }: { programClasses: readonly string[]; onClass: (klass: string, confidence: number) => void; su: boolean }) {
  const { t } = useI18n();
  const video = useRef<HTMLVideoElement>(null);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const model = useRef<Classifier | null>(null);
  const [kind, setKind] = useState<Classifier["kind"] | null>(null);
  const [classes, setClasses] = useState(() => (programClasses.length ? programClasses : [t("student.microbit.ai.class1"), t("student.microbit.ai.class2")]).map(newClass));
  const [on, setOn] = useState(false);
  const [err, setErr] = useState(false);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [pred, setPred] = useState<{ label: string; confidence: number }>({ label: "", confidence: 0 });
  const canCam = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
  const nameOf = useCallback((id: string) => classes.find((c) => c.id === id)?.name ?? "", [classes]);
  const onClassRef = useRef(onClass);
  onClassRef.current = onClass;

  useEffect(() => {
    let alive = true;
    void createClassifier().then((c) => { if (alive) { model.current = c; setKind(c.kind); } });
    return () => { alive = false; };
  }, []);

  const grab = useCallback((): HTMLCanvasElement | null => {
    const v = video.current;
    if (!v || v.videoWidth === 0) return null;
    if (!canvas.current) canvas.current = document.createElement("canvas");
    const c = canvas.current; c.width = INPUT; c.height = INPUT;
    const ctx = c.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(v, 0, 0, INPUT, INPUT);
    return c;
  }, []);

  const start = useCallback(async () => {
    setErr(false);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
      stream.current = s;
      if (video.current) { video.current.srcObject = s; await video.current.play().catch(() => {}); }
      setOn(true);
    } catch {
      setErr(true);
    }
  }, []);

  const capture = useCallback(async (id: string) => {
    const c = grab(); const m = model.current;
    if (!c || !m) return;
    await m.addSample(id, c);
    setCounts((prev) => ({ ...prev, [id]: m.sampleCount(id) }));
  }, [grab]);

  const rename = (id: string, name: string) => setClasses((cs) => cs.map((c) => (c.id === id ? { ...c, name } : c)));
  const addClass = () => setClasses((cs) => [...cs, newClass(t("student.microbit.ai.classN", { n: cs.length + 1 }))]);
  const removeClass = (id: string) => { model.current?.forget(id); setClasses((cs) => cs.filter((c) => c.id !== id)); };
  /** 프로그램이 기다리는 이름으로 맞추기 — 담은 사진은 차례대로 그대로 둔다 */
  const matchProgram = () => setClasses((cs) => programClasses.map((name, i) => (cs[i] ? { ...cs[i]!, name } : newClass(name))));

  // 라이브 추론 — 약 8번/초(저사양 · 발열 배려). 문턱 아래는 빈 이름
  useEffect(() => {
    if (!on) return;
    let alive = true;
    const loop = async () => {
      while (alive) {
        const c = grab(); const m = model.current;
        if (c && m && m.labels().length > 0) {
          const p = await m.classify(c);
          if (!alive) break;
          const name = p.confidence >= CONF_MIN ? nameOf(p.label) : "";
          setPred({ label: name, confidence: p.confidence });
          onClassRef.current(name, Math.round(p.confidence * 100));
        }
        await new Promise((r) => setTimeout(r, 120));
      }
    };
    void loop();
    return () => { alive = false; };
  }, [on, grab, nameOf]);

  useEffect(() => () => { stream.current?.getTracks().forEach((tr) => tr.stop()); }, []);

  const mismatch = programClasses.length > 0 && programClasses.some((n) => !classes.some((c) => c.name.trim() === n));
  return (
    <div className="pf-mb__ai-camera" data-testid="fs-mb-ai-camera">
      <p className="pf-mb__hint">
        <span className="pf-mb__chip">{kind === "tf" ? t("student.microbit.ai.modeTf") : kind === "simple" ? t("student.microbit.ai.modeSimple") : t("student.microbit.ai.modeLoading")}</span>{" "}
        {t("student.microbit.ai.private")}
      </p>
      {!canCam ? <p className="pf-mb__hint" role="alert">{t("student.microbit.ai.noCamera")}</p> : null}
      {err ? <p className="pf-mb__hint" role="alert" data-testid="fs-mb-ai-cam-error">{t("student.microbit.ai.camError")}</p> : null}
      <div className="pf-mb__ai-cam-row">
        <div className="pf-mb__ai-video">
          <video ref={video} playsInline muted />
          {!on && canCam ? <Button variant="accent" icon="📷" onClick={() => void start()} data-testid="fs-mb-ai-cam-start">{t("student.microbit.ai.camStart")}</Button> : null}
        </div>
        <div className="pf-mb__ai-classes">
          <p className="pf-mb__hint">{t("student.microbit.ai.how")}</p>
          {classes.map((c) => (
            <div key={c.id} className="pf-mb__ai-class">
              <input value={c.name} onChange={(e) => rename(c.id, e.target.value)} aria-label={t("student.microbit.ai.className")} maxLength={20} data-testid="fs-mb-ai-class-name" />
              <Button variant="ghost" disabled={!on} onClick={() => void capture(c.id)} data-testid="fs-mb-ai-capture">{t("student.microbit.ai.capture")}</Button>
              <span className="pf-mb__hint">{t("student.microbit.ai.count", { n: counts[c.id] ?? 0 })}</span>
              <button type="button" className="pf-mb__ai-remove" onClick={() => removeClass(c.id)} aria-label={t("student.microbit.ai.remove")}>×</button>
            </div>
          ))}
          <div className="pf-mb__sim-actions">
            <Button variant="ghost" onClick={addClass} data-testid="fs-mb-ai-class-add">{t("student.microbit.ai.add")}</Button>
            {mismatch ? <Button variant="ghost" onClick={matchProgram} data-testid="fs-mb-ai-match">{t("student.microbit.ai.match")}</Button> : null}
          </div>
          <p className="pf-mb__scene-state" data-testid="fs-mb-ai-live">
            {t("student.microbit.ai.seeing")} <b>{pred.label || t("student.microbit.ai.unknown")}</b>
            {su && pred.label ? <span className="pf-mb__hint"> · {t("student.microbit.ai.confidence", { n: Math.round(pred.confidence * 100) })}</span> : null}
          </p>
        </div>
      </div>
    </div>
  );
}
